import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Facebook, Instagram, Youtube, Mail, Phone, MapPin, Cookie, Clock } from 'lucide-react';
import Logo from './Logo';
import { getLocalizedPath, getHomePath } from '../utils/routes';
import { useTranslations } from '../context/TranslationsContext';

const Footer = () => {
  const { t, i18n } = useTranslation();
  const { td } = useTranslations();
  const currentYear = new Date().getFullYear();

  // Helper for localized paths
  const lp = (key) => getLocalizedPath(key, i18n.language);

  return (
    <footer className="foreground mt-20">
      <div className="foreground opacity-20 border-t" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <Link to={getHomePath(i18n.language)}>
              <Logo className="mb-4 h-12 w-auto" />
            </Link>
            <p className="text-muted-foreground text-sm mb-6">
              {td('footer.subtitle')}
            </p>
            <div className="flex gap-4">
              <a 
                href="https://facebook.com/avepark" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
                data-testid="footer-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="https://instagram.com/avepark" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
                data-testid="footer-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://youtube.com/avepark" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
                data-testid="footer-youtube"
              >
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-4">{td('nav.activities')}</h4>
            <div className="flex flex-col gap-3">
              <Link to={lp('classes')} className="text-muted-foreground hover:text-foreground transition-colors text-sm">
                {td('nav.classes')}
              </Link>
              <Link to={lp('birthdays')} className="text-muted-foreground hover:text-foreground transition-colors text-sm">
                {td('nav.birthdays')}
              </Link>
              <Link to={lp('physio')} className="text-muted-foreground hover:text-foreground transition-colors text-sm">
                {td('nav.physio')}
              </Link>
              <Link to={lp('passes')} className="text-muted-foreground hover:text-foreground transition-colors text-sm">
                {td('nav.passes')}
              </Link>
            </div>
          </div>

          {/* Information */}
          <div>
            <h4 className="font-semibold mb-4">{td('contact.title')}</h4>
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3 text-muted-foreground text-sm">
                <MapPin className="w-4 h-4 shrink-0" />
                <span>ul. Krakowiaków 80/98, Warszawa</span>
              </div>
              <div className="flex items-center gap-3 text-muted-foreground text-sm">
                <Phone className="w-4 h-4 shrink-0" />
                <span>+48 797 254 850</span>
              </div>
              <div className="flex items-center gap-3 text-muted-foreground text-sm">
                <Mail className="w-4 h-4 shrink-0" />
                <span>kontakt@avepark.pl</span>
              </div>
              <div className="flex items-center gap-3 text-muted-foreground text-sm">
                <Clock className="w-4 h-4 shrink-0" />
                <Link to={lp('contact')} className="text-muted-foreground hover:text-foreground transition-colors text-sm">
                {td('contact.hours')}
              </Link>
              </div>
            </div>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold mb-4">{td('contact.documents')}</h4>
            <div className="flex flex-col gap-3">
              <Link to={lp('document/polityka-prywatnosci')} className="text-muted-foreground hover:text-foreground transition-colors text-sm">
                {td('footer.privacy')}
              </Link>
              <Link to={lp('document/regulamin-serwisu')} className="text-muted-foreground hover:text-foreground transition-colors text-sm">
                {td('footer.terms')}
              </Link>
              <Link to={lp('faq')} className="text-muted-foreground hover:text-foreground transition-colors text-sm">
                {td('nav.faq')}
              </Link>
              <Link 
                to={i18n.language === 'pl' ? '/pl/aktywuj-karnet' : '/en/activate-pass'} 
                className="text-muted-foreground hover:text-foreground transition-colors text-sm"
                data-testid="footer-activate-pass"
              >
                {td('footer.activateGiftPass', 'Aktywuj karnet prezentowy')}
              </Link>
            </div>
          </div>
        </div>

        <div className="foreground opacity-20 mt-12 border-t" />
        <div className="foreground pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-muted-foreground text-sm">
            © {currentYear} AvePark. {td('footer.rights')}.
          </p>
          <p className="text-muted-foreground text-xs">
            <button
                onClick={() => window.openCookieConsent && window.openCookieConsent()}
                className="text-muted-foreground hover:text-foreground transition-colors text-sm text-left flex items-center gap-1.5"
                data-testid="footer-cookie-settings"
              >
                <Cookie className="w-3.5 h-3.5" />
                {td('footer.cookieSettings', 'Ustawienia cookies')}
              </button>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
